import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Phone, Mail, MapPin, Calendar, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formType, setFormType] = useState('quick'); // 'quick' or 'detailed'
  const [quickFormData, setQuickFormData] = useState({
    name: '',
    email: '',
    phone: '',
    homeValue: '',
    age: '',
    mortgageBalance: ''
  });
  const [formData, setFormData] = useState({
    // Borrower Info
    borrowerFirstName: '',
    borrowerLastName: '',
    borrowerDOB: '',
    borrowerEmail: '',
    borrowerPhone: '',
    
    // Co-Borrower Info
    coBorrowerFirstName: '',
    coBorrowerLastName: '',
    coBorrowerDOB: '',
    
    // Financial Info
    monthlyIncome: '',
    totalAssets: '',
    monthlyHousingExpenses: '',
    monthlyDebtExpenses: '',
    householdSize: '',
    creditEvaluation: '',
    
    // Property Info
    propertyAddress: '',
    squareFootage: '',
    propertyType: '',
    marketValue: '',
    firstMortgageBalance: '',
    secondMortgageBalance: '',
    
    // Purpose
    purpose: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quick-quote`, quickFormData);
      // Redirect to thank you page for conversion tracking
      window.location.href = '/thank-you';
    } catch (error) {
      // Error logged silently - user sees alert
      alert('Failed to submit. Please try again or call us at (720) 307-8711');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quote-request`, formData);
      // Redirect to thank you page for conversion tracking
      window.location.href = '/thank-you';
    } catch (error) {
      // Error logged silently - user sees alert
      alert('Failed to submit. Please try again or call us at (720) 307-8711');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuickChange = (e) => {
    setQuickFormData({
      ...quickFormData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Get Your Personalized Quote
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to explore your options? Fill out the form below and I'll provide 
            a personalized analysis of your reverse mortgage benefits.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Phone Card */}
            <Card className="hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Call or Text Me</h3>
                    <a href="tel:+17203078711" className="text-blue-600 hover:text-blue-700 font-medium">
                      (720) 307-8711
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Mon-Fri: 8am - 6pm MT</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email Me</h3>
                    <a href="mailto:info@milehighreverse.com" className="text-blue-600 hover:text-blue-700 font-medium break-all">
                      info@milehighreverse.com
                    </a>
                    <p className="text-sm text-gray-600 mt-1">I'll typically respond within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location Card */}
            <Card className="hover:shadow-xl transition-shadow border-2">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Serving Colorado</h3>
                    <p className="text-gray-700">Denver, Colorado Springs, Boulder, Fort Collins & Statewide</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Calendar Booking Card */}
            <Card className="bg-blue-600 text-white hover:bg-blue-700 transition-colors border-2 border-blue-600">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Schedule a Call</h3>
                    <p className="text-sm text-blue-100 mb-3">Pick a time that works for you</p>
                    <Button 
                      variant="secondary"
                      className="w-full"
                      onClick={() => window.open('https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3-Q5PB32iYcYOFy0AfHcJQEewh-0EC12gx8qLVXkzCXMl3Ok1RTHDMZLWUv-lyg1PdwLj-W9SR', '_blank')}
                    >
                      View Calendar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-2xl border-2">
              <CardHeader className="bg-gray-50">
                <CardTitle className="text-2xl">Get Your Free Quote</CardTitle>
                
                {/* Form Type Toggle */}
                <div className="flex gap-2 mt-4">
                  <Button
                    type="button"
                    variant={formType === 'quick' ? 'default' : 'outline'}
                    onClick={() => setFormType('quick')}
                    className={formType === 'quick' ? 'bg-blue-600' : ''}
                  >
                    Quick Quote
                  </Button>
                  <Button
                    type="button"
                    variant={formType === 'detailed' ? 'default' : 'outline'}
                    onClick={() => setFormType('detailed')}
                    className={formType === 'detailed' ? 'bg-blue-600' : ''}
                  >
                    Detailed Analysis
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  {formType === 'quick' 
                    ? '⚡ Get a Quick Estimate' 
                    : '📊 Comprehensive analysis for accurate quote'}
                </p>
              </CardHeader>
              <CardContent className="p-8">
                {!isSubmitted ? (
                  <>
                    {/* Quick Quote Form */}
                    {formType === 'quick' && (
                      <form onSubmit={handleQuickSubmit} className="space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={quickFormData.name}
                            onChange={handleQuickChange}
                            required
                            placeholder="John Smith"
                            className="h-12 text-base"
                          />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={quickFormData.email}
                              onChange={handleQuickChange}
                              required
                              placeholder="john@example.com"
                              className="h-12 text-base"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone *</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={quickFormData.phone}
                              onChange={handleQuickChange}
                              required
                              placeholder="(720) 555-1234"
                              className="h-12 text-base"
                              />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="homeValue">Home Value *</Label>
                            <Input
                              id="homeValue"
                              name="homeValue"
                              type="text"
                              value={quickFormData.homeValue}
                              onChange={handleQuickChange}
                              required
                              placeholder="$500,000"
                              className="h-12 text-base"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="mortgageBalance">Current Mortgage Balance *</Label>
                            <Input
                              id="mortgageBalance"
                              name="mortgageBalance"
                              type="text"
                              value={quickFormData.mortgageBalance}
                              onChange={handleQuickChange}
                              required
                              placeholder="$150,000 (or $0)"
                              className="h-12 text-base"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="age">Your Age *</Label>
                          <Input
                            id="age"
                            name="age"
                            type="number"
                            value={quickFormData.age}
                            onChange={handleQuickChange}
                            required
                            placeholder="68"
                            className="h-12 text-base"
                          />
                        </div>

                        <Button 
                          type="submit"
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg"
                        >
                          Get Quick Estimate
                        </Button>

                        <p className="text-center text-sm text-gray-600">
                          Want a more accurate quote? Switch to <button type="button" onClick={() => setFormType('detailed')} className="text-blue-600 hover:underline font-semibold">Detailed Analysis</button>
                        </p>

                        {/* Privacy Notice */}
                        <div className="flex items-start space-x-3 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <div className="text-sm">
                            <p className="text-gray-800 font-semibold mb-1">Your Privacy is Protected</p>
                            <p className="text-gray-600">Your data is secure and will not be sold to third parties.</p>
                          </div>
                        </div>
                      </form>
                    )}

                    {/* Detailed Analysis Form */}
                    {formType === 'detailed' && (
                  <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Borrower Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-100">
                        Borrower Information
                      </h3>
                      <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="borrowerFirstName">First Name *</Label>
                            <Input
                              id="borrowerFirstName"
                              name="borrowerFirstName"
                              value={formData.borrowerFirstName}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="borrowerLastName">Last Name *</Label>
                            <Input
                              id="borrowerLastName"
                              name="borrowerLastName"
                              value={formData.borrowerLastName}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="borrowerDOB">Date of Birth *</Label>
                            <Input
                              id="borrowerDOB"
                              name="borrowerDOB"
                              type="date"
                              value={formData.borrowerDOB}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="borrowerEmail">Email *</Label>
                            <Input
                              id="borrowerEmail"
                              name="borrowerEmail"
                              type="email"
                              value={formData.borrowerEmail}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="borrowerPhone">Phone *</Label>
                            <Input
                              id="borrowerPhone"
                              name="borrowerPhone"
                              type="tel"
                              value={formData.borrowerPhone}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Co-Borrower Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-100">
                        Co-Borrower Information <span className="text-sm font-normal text-gray-500">(if applicable)</span>
                      </h3>
                      <div className="space-y-4">
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="coBorrowerFirstName">First Name</Label>
                            <Input
                              id="coBorrowerFirstName"
                              name="coBorrowerFirstName"
                              value={formData.coBorrowerFirstName}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="coBorrowerLastName">Last Name</Label>
                            <Input
                              id="coBorrowerLastName"
                              name="coBorrowerLastName"
                              value={formData.coBorrowerLastName}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="coBorrowerDOB">Date of Birth</Label>
                            <Input
                              id="coBorrowerDOB"
                              name="coBorrowerDOB"
                              type="date"
                              value={formData.coBorrowerDOB}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-100">
                        Financial Information
                      </h3>
                      <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="monthlyIncome">Total Monthly Household Income *</Label>
                            <Input
                              id="monthlyIncome"
                              name="monthlyIncome"
                              type="number"
                              placeholder="$5,000"
                              value={formData.monthlyIncome}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="totalAssets">Total Household Assets *</Label>
                            <Input
                              id="totalAssets"
                              name="totalAssets"
                              type="number"
                              placeholder="$100,000"
                              value={formData.totalAssets}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="monthlyHousingExpenses">Total Monthly Housing Expenses *</Label>
                            <Input
                              id="monthlyHousingExpenses"
                              name="monthlyHousingExpenses"
                              type="number"
                              placeholder="$2,000"
                              value={formData.monthlyHousingExpenses}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="monthlyDebtExpenses">Other Monthly Debt Expenses *</Label>
                            <Input
                              id="monthlyDebtExpenses"
                              name="monthlyDebtExpenses"
                              type="number"
                              placeholder="$500"
                              value={formData.monthlyDebtExpenses}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="householdSize">Number of People in Household *</Label>
                            <Input
                              id="householdSize"
                              name="householdSize"
                              type="number"
                              placeholder="2"
                              value={formData.householdSize}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="creditEvaluation">Credit Evaluation *</Label>
                            <select
                              id="creditEvaluation"
                              name="creditEvaluation"
                              value={formData.creditEvaluation}
                              onChange={handleChange}
                              required
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <option value="">Select...</option>
                              <option value="good">Good (700+)</option>
                              <option value="fair">Fair (620-699)</option>
                              <option value="poor">Poor (Below 620)</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Property Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-100">
                        Property Information
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="propertyAddress">Property Address *</Label>
                          <Input
                            id="propertyAddress"
                            name="propertyAddress"
                            placeholder="123 Main St, Denver, CO 80202"
                            value={formData.propertyAddress}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="squareFootage">Square Footage *</Label>
                            <Input
                              id="squareFootage"
                              name="squareFootage"
                              type="number"
                              placeholder="2,000"
                              value={formData.squareFootage}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="propertyType">Type of Property *</Label>
                            <select
                              id="propertyType"
                              name="propertyType"
                              value={formData.propertyType}
                              onChange={handleChange}
                              required
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <option value="">Select...</option>
                              <option value="sfh">Single Family Home</option>
                              <option value="condo">Condo</option>
                              <option value="townhouse">Townhouse</option>
                              <option value="multi">2-4 Unit Property</option>
                              <option value="manufactured">Manufactured Home</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="marketValue">Market Value *</Label>
                            <Input
                              id="marketValue"
                              name="marketValue"
                              type="number"
                              placeholder="$500,000"
                              value={formData.marketValue}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="firstMortgageBalance">1st Mortgage Balance *</Label>
                            <Input
                              id="firstMortgageBalance"
                              name="firstMortgageBalance"
                              type="number"
                              placeholder="$150,000"
                              value={formData.firstMortgageBalance}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="secondMortgageBalance">2nd Mortgage Balance</Label>
                            <Input
                              id="secondMortgageBalance"
                              name="secondMortgageBalance"
                              type="number"
                              placeholder="$0"
                              value={formData.secondMortgageBalance}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Purpose */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b-2 border-blue-100">
                        Purpose
                      </h3>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="purpose">What is your goal? *</Label>
                          <select
                            id="purpose"
                            name="purpose"
                            value={formData.purpose}
                            onChange={handleChange}
                            required
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <option value="">Select...</option>
                            <option value="stay">Stay in current home</option>
                            <option value="purchase">Purchase using a reverse mortgage</option>
                            <option value="refinance">Refinance existing reverse mortgage</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="message">Additional Questions or Comments</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Any additional information you'd like to share..."
                            className="resize-none"
                          />
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 text-lg"
                    >
                      Request My Free Quote
                    </Button>

                    <p className="text-center text-sm text-gray-600">
                      Just need a quick estimate? Try our <button type="button" onClick={() => setFormType('quick')} className="text-blue-600 hover:underline font-semibold">Quick Quote</button>
                    </p>

                    {/* Privacy Notice */}
                    <div className="flex items-start space-x-3 bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mt-4">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <div className="text-sm">
                        <p className="text-gray-800 font-semibold mb-1">Your Privacy is Protected</p>
                        <p className="text-gray-600">Your data is secure and will not be sold to third parties. We use your information solely to provide you with a personalized reverse mortgage quote.</p>
                      </div>
                    </div>
                  </form>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h3>
                    <p className="text-xl text-gray-600 mb-2">
                      I've received your request for a personalized quote.
                    </p>
                    <p className="text-gray-600">
                      I'll review your information and get back to you within 2-4 hours 
                      with a detailed analysis.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
